/**
 * 고객 보고서(산출물) — 계약 타입을 화면이 쓰는 이름으로 재수출한다.
 * ★ 1단계의 `lib/mock/*` 를 대신한다. 화면은 **서버가 정한 회차**(`cycle`)를 표시만 하고
 *   날짜를 계산하지 않는다 — 경계 규칙이 화면으로 새지 않게.
 */
import type { works } from '@geo-admin/shared/contract/api/index.ts';
import { VIDEO_PLATFORMS, type VideoPlatform } from '@geo-admin/shared/contract/probe-values.ts';
import type { z } from 'zod';
import { useCallback, useEffect, useState } from 'react';

export type WorksView = z.infer<typeof works.getWorks.response>;
export type HomepageView = WorksView['homepages'][number];
export type PostView = HomepageView['posts'][number];
export type PressView = WorksView['press'][number];
export type VideoView = WorksView['videos'][number];
export type ReviewView = WorksView['reviews'][number];
export type RankingView = WorksView['rankings'][number];
export type Platform = VideoPlatform;

export const PLATFORM_LABEL: Record<Platform, string> = {
  youtube: '유튜브 쇼츠',
  instagram: '인스타',
  x: 'X',
  threads: '스레드',
  tiktok: '틱톡',
};

export { VIDEO_PLATFORMS };

/**
 * 유튜브 게시물 URL → 썸네일 주소. **페치가 아니라 문자열 변환**이다.
 * ★ 서버는 외부 URL 을 가져오지 않는다(SSRF — 메타데이터 서버가 닿는다). 브라우저도 og:image 를
 *   **파싱할 수 없다**(CSP `connect-src: self` + CORS). 유도가 가능한 유일한 곳이 여기다.
 */
export function youtubeThumb(url: string): string | null {
  const m =
    /(?:youtube\.com\/shorts\/|youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/.exec(url);
  return m?.[1] ? `https://i.ytimg.com/vi/${m[1]}/hqdefault.jpg` : null;
}

/** 영상의 대표 이미지 — 보낸 값이 우선, 없으면 유튜브 링크에서 유도한다 */
export function videoCover(v: VideoView): string | null {
  if (v.coverUrl) return v.coverUrl;
  for (const p of v.posts) {
    if (p.platform === 'youtube') {
      const t = youtubeThumb(p.url);
      if (t) return t;
    }
  }
  return null;
}

/**
 * 회차 넘김 상태 — **처음 여는 회차는 서버가 정한다**(`openBack` = 어제가 든 회차, 261020-report-cycle-yesterday-basis).
 * 9/20 구독이면 10/20(구독일 당일)에는 막 끝난 9/20~10/19 를, 10/21 부터는 진행 중인 10/20~11/19 를 연다.
 * ★ 번호(`back`)는 그대로 오늘 기준이다 — 구독일 당일에도 다음 칸(0번)에서 그날 올린 산출물이 보인다.
 * ★ 화면이 날짜를 계산하지 않는다. 첫 응답의 `openBack` 을 받아 둘 뿐이다(`learn`).
 *   0번은 `cycle` 을 빼고 묻는다 — 서버의 기본값이 0 이라 첫 요청과 같은 키가 되어 다시 묻지 않는다.
 * `resetKey`(브랜드)가 바뀌면 처음으로 돌아간다 — 구독일이 브랜드마다 다르다.
 */
export function useCycleBack(resetKey: string) {
  const [picked, setPicked] = useState<number>();
  const [open, setOpen] = useState<number>();
  // biome-ignore lint/correctness/useExhaustiveDependencies: 브랜드가 바뀔 때만 되돌린다
  useEffect(() => {
    setPicked(undefined);
    setOpen(undefined);
  }, [resetKey]);
  const back = picked ?? open ?? 0;
  // 응답이 올 때마다 부른다 — 이미 알면 그대로 둔다(사람이 고른 회차를 덮지 않게 `picked` 와 따로 둔다)
  const learn = useCallback((openBack: number | undefined) => {
    if (openBack !== undefined) setOpen((o) => o ?? openBack);
  }, []);
  return {
    back,
    /** 요청의 `cycle` — 0 이면 뺀다 */
    param: back === 0 ? undefined : back,
    /** 처음 여는 회차를 안다 — 이 전에 회차 단위 지표를 물으면 구독일 당일에 한 번 헛걸음한다 */
    ready: picked !== undefined || open !== undefined,
    setBack: setPicked,
    learn,
  };
}
